import React, { useState } from "react";
import { toast } from "react-toastify";

import { AirtimeProvider } from "../../../../features/auth/store/types/airtime-types";
import { MdHelpOutline, MdOutlineDelete } from "react-icons/md";
import { FiEdit3 } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  fetchAirtimeProviders,
  updateAirtimeProvider,
} from "../../../../features/auth/store/airtimeProviderSlice";
import { buildProviderStatusFormData } from "../../statusToggleUtils";
import { StatusSwitch } from "../../../../components/Reusable/ToggleSwitch";

interface Props {
  providers: AirtimeProvider[];
  loading: boolean;
  searchTerm: string;
  statusFilter: string;
  onDelete: (id: number) => void;
  onEdit: (id: number) => void;
  onManageFaq: (provider: { id: number; name: string }) => void;
}

const AirtimeProvidersTable: React.FC<Props> = ({
  providers,
  loading,
  searchTerm,
  statusFilter,
  onDelete,
  onEdit,
  onManageFaq,
}) => {
  const {
    pagination,
    lastPage: totalPages,
    currentPage,
  } = useSelector((state: RootState) => state.airtimeProviders);
  const dispatch = useDispatch<AppDispatch>();
  const [updatingProviderIds, setUpdatingProviderIds] = useState<number[]>([]);
  const [updatingStockIds, setUpdatingStockIds] = useState<number[]>([]);
  // Calculate pagination values
  const { nextPageUrl, prevPageUrl } = pagination;

  // Handle page navigation
  const goToNextPage = () => {
    if (currentPage < totalPages && nextPageUrl !== null) {
      dispatch(
        fetchAirtimeProviders({
          url: nextPageUrl,
          search: searchTerm.trim() || undefined,
          status: statusFilter || undefined,
        }),
      );
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1 && prevPageUrl !== null) {
      dispatch(
        fetchAirtimeProviders({
          url: prevPageUrl,
          search: searchTerm.trim() || undefined,
          status: statusFilter || undefined,
        }),
      );
    }
  };

  const handleStatusToggle = async (provider: AirtimeProvider) => {
    const nextStatus = provider.status === "on" ? "off" : "on";

    setUpdatingProviderIds((prev) => [...prev, provider.id]);

    try {
      await dispatch(
        updateAirtimeProvider({
          id: provider.id,
          data: buildProviderStatusFormData(provider, nextStatus),
        }),
      ).unwrap();
      toast.success(
        `Provider ${nextStatus === "on" ? "activated" : "deactivated"} successfully`,
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to update provider status");
    } finally {
      setUpdatingProviderIds((prev) => prev.filter((id) => id !== provider.id));
    }
  };

  const handleOutOfStockToggle = async (airtimeProvider: AirtimeProvider) => {
    const nextStockStatus = !airtimeProvider.out_of_stock;

    setUpdatingStockIds((prev) => [...prev, airtimeProvider.id]);
    const plan = { ...airtimeProvider, out_of_stock: nextStockStatus };

    try {
      await dispatch(
        updateAirtimeProvider({
          id: airtimeProvider.id,
          data: buildProviderStatusFormData(plan, airtimeProvider.status),
        }),
      ).unwrap();
      toast.success(
        `Provider plans marked as ${nextStockStatus ? "out of stock" : "in stock"}`,
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to update stock status");
    } finally {
      setUpdatingStockIds((prev) =>
        prev.filter((id) => id !== airtimeProvider.id),
      );
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  if (!providers.length) return <div>No providers found.</div>;

  return (
    <div className="flex flex-col w-full">
      <div className="overflow-x-auto pb-4">
        <table className="w-full min-w-[800px] border border-[#FFFFFF1A] rounded-2xl overflow-hidden font-Inter">
          <thead>
            <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
              <th className="px-3 md:px-6 text-left">Status</th>
              <th className="px-3 md:px-6 text-left">In Stock</th>
              <th className="px-3 md:px-6 text-left">Name</th>
              <th className="px-3 md:px-6 text-left hidden md:table-cell">
                Slug
              </th>
              <th className="px-3 md:px-6 text-left hidden md:table-cell">
                Description
              </th>
              <th className="px-3 md:px-6 text-left hidden md:table-cell">
                Verify Recipient
              </th>
              <th className="px-3 md:px-6 text-left">Image</th>
              <th className="px-3 md:px-6 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm">
            {providers.map((provider) => (
              <tr
                key={provider.id}
                className="border-b border-gray-200 h-[50px]"
              >
                <td className="px-3 md:px-6 py-2 align-middle">
                  <StatusSwitch
                    checked={provider.status === "on"}
                    disabled={updatingProviderIds.includes(provider.id)}
                    onChange={() => handleStatusToggle(provider)}
                    label={`Toggle ${provider.name} status`}
                  />
                </td>
                <td className="px-6 py-4 align-middle">
                  <StatusSwitch
                    checked={!provider.out_of_stock}
                    disabled={updatingStockIds.includes(provider.id)}
                    onChange={() => handleOutOfStockToggle(provider)}
                    label={`Toggle ${provider.name || "plans"} stock status`}
                    activeText="Available"
                    inactiveText="Unavailable"
                  />
                </td>
                <td className="px-3 md:px-6 py-2 align-middle truncate max-w-[100px]">
                  {provider.name}
                </td>
                <td className="px-3 md:px-6 py-2 align-middle hidden md:table-cell">
                  {provider.slug || "-"}
                </td>
                <td className="px-3 md:px-6 py-2 align-middle hidden md:table-cell max-w-[200px] truncate">
                  {provider.description ? (
                    <div
                      className="[&_a]:text-blue-600 dark:[&_a]:text-blue-400 [&_a]:no-underline"
                      dangerouslySetInnerHTML={{ __html: provider.description }}
                    />
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-3 md:px-6 py-2 align-middle hidden md:table-cell">
                  {provider.verify_recipient ? "Yes" : "No"}
                </td>
                <td className="px-3 md:px-6 py-2 align-middle">
                  {provider.image_url ? (
                    <img
                      src={provider.image_url}
                      alt={provider.name}
                      className="w-8 h-8 object-cover rounded"
                    />
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-3 md:px-6 py-2 align-middle">
                  <div className="flex items-center gap-2 h-full">
                    <button
                      className="hover:text-gray-600 transition-colors"
                      onClick={() =>
                        onManageFaq({ id: provider.id, name: provider.name })
                      }
                      title="Manage FAQ"
                    >
                      <MdHelpOutline className="text-xl" />
                    </button>
                    <button
                      className="text-blue-600"
                      onClick={() => onEdit(provider.id)}
                    >
                      <FiEdit3 className="text-xl" />
                    </button>
                    <button
                      className="text-red-600"
                      onClick={() => onDelete(provider.id)}
                    >
                      <MdOutlineDelete className="text-xl" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center border-t border-[#FFFFFF1A]">
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 disabled:cursor-not-allowed"`}
          onClick={goToPreviousPage}
          disabled={loading || currentPage === 1 || !prevPageUrl}
        >
          Previous
        </button>
        <span className="text-gray-600 dark:text-white text-xs bp:text-sm">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 disabled:cursor-not-allowed"`}
          onClick={goToNextPage}
          disabled={
            loading ||
            currentPage === totalPages ||
            totalPages === 0 ||
            !nextPageUrl
          }
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AirtimeProvidersTable;
